import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { confirmAction } from '../../utils/confirmToast';
import { Car, Plus, Key, Calendar, Tag, Edit2, Trash2, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './MyVehicles.module.css';

const MyVehicles = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    type: 'Car',
    model: '',
    number: '',
    color: ''
  });

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/users/vehicles');
      setVehicles(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch vehicles');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditClick = (vehicle) => {
    setEditingId(vehicle._id);
    setFormData({
      type: vehicle.type,
      model: vehicle.model,
      number: vehicle.number,
      color: vehicle.color || ''
    });
    setIsDrawerOpen(true);
  };

  const handleDelete = async (id) => {
    confirmAction("Are you sure you want to remove this vehicle?", async () => {
      try {
        await api.delete(`/users/vehicles/${id}`);
        setVehicles(vehicles.filter(v => v._id !== id));
        toast.success('Vehicle deleted successfully');
      } catch (error) {
        toast.error('Failed to delete vehicle');
      }
    });
  };

  const handleOpenDrawer = () => {
    setEditingId(null);
    setFormData({ type: 'Car', model: '', number: '', color: '' });
    setIsDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { data } = await api.put(`/users/vehicles/${editingId}`, formData);
        setVehicles(vehicles.map(v => v._id === editingId ? data : v));
      } else {
        const { data } = await api.post('/users/vehicles', formData);
        setVehicles([data, ...vehicles]);
      }
      toast.success(editingId ? 'Vehicle updated successfully' : 'Vehicle registered successfully');
      setIsDrawerOpen(false);
    } catch (error) {
      toast.error('Failed to save vehicle');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="My Vehicles" 
        breadcrumb={['Services', 'Vehicles']} 
        actionButton={
          <button className={styles.addBtn} onClick={handleOpenDrawer}>
            <Plus size={16} /> Register Vehicle
          </button>
        }
      />

      <div className={styles.grid}>
        {loading ? <p>Loading vehicles...</p> : vehicles.map(vehicle => {
          const Icon = vehicle.type === 'Car' ? Car : Key;
          return (
            <div key={vehicle._id} className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.iconCircle}>
                  <Icon size={24} />
                </div>
                <div className={styles.actionButtons}>
                  <button className={styles.iconBtn} onClick={() => handleEditClick(vehicle)} title="Edit">
                    <Edit2 size={16} />
                  </button>
                  <button className={styles.iconBtn} onClick={() => handleDelete(vehicle._id)} title="Delete" style={{color: '#e74c3c'}}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className={styles.cardBody}>
                <h3>{vehicle.number}</h3>
                <p>{vehicle.model}</p>
                <span className={`${styles.statusBadge} ${vehicle.sticker === 'VALID' ? styles.valid : styles.pending}`}>
                  <ShieldCheck size={12} /> Sticker: {vehicle.sticker}
                </span>
              </div>
              
              <div className={styles.cardFooter}>
                <div className={styles.footerItem}>
                  <Tag size={14} className={styles.icon} />
                  <span>{vehicle.color}</span>
                </div>
                <div className={styles.footerItem}>
                  <Car size={14} className={styles.icon} />
                  <span>{vehicle.type}</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add New Card */}
        <div className={styles.addCard} onClick={handleOpenDrawer}>
          <div className={styles.addIcon}>
            <Plus size={24} />
          </div>
          <span>Register New Vehicle</span>
        </div>
      </div>

      <Drawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        title={editingId ? "Edit Vehicle" : "Register Vehicle"}
      >
        <form className="drawerForm" onSubmit={handleSubmit}>
          <div className="formGroup">
            <label>Vehicle Type</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="Car">Car</option>
              <option value="Bike">Two Wheeler</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="formGroup">
            <label>Make & Model</label>
            <input name="model" value={formData.model} onChange={handleChange} required placeholder="e.g. Honda City" />
          </div>
          <div className="formGroup">
            <label>License Plate Number</label>
            <input name="number" value={formData.number} onChange={handleChange} required placeholder="e.g. MH 12 AB 1234" />
          </div>
          <div className="formGroup">
            <label>Color</label>
            <input name="color" value={formData.color} onChange={handleChange} required placeholder="e.g. White" />
          </div>
          
          <div className="formActions">
            <button type="button" className="btnCancel" onClick={() => setIsDrawerOpen(false)}>Cancel</button>
            <button type="submit" className="btnSubmit">{editingId ? "Save Changes" : "Register"}</button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default MyVehicles;
